import React, { useState } from "react";
import { Box, Container, IconButton, Tooltip } from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { QK } from "../../base/qk";
import { getVisitPlan } from "../../api/services/visitplan";
import { deleteVisitByID } from "../../api/services/scheduling";
import FilterComponent from "./FilterComponent";

import ScheduleModal from "./VisitPlanModal/ScheduleModal";
import UpdateVisitModal from "../../components/Clients/ClientDetails/WhatElse/VisitAnalysis/UpdateVisitModal";
import EditScheduleModal from "./VisitPlanModal/EditScheduleModal";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import { useSnackbar } from "notistack";
import { useUserProfile } from "../../hooks/userProfile";
import {
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  CheckCircle,
  Delete,
  PersonAddDisabledRounded,
} from "@mui/icons-material";
import TableComponent from "../../components/TableComponent/TableComponent";

const VisitPlanScreen = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    client: "",
    career: "",
    location: "",
    area: "",
    startDate: "",
    endDate: "",
    careerAssigned: "",
  });

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showScheduleUpdateModal, setShowScheduleUpdateModal] = useState(false);
  const [area, setArea] = useState(null);
  const [visitId, setVisitId] = useState(null);
  const [clientId, setClientId] = useState(null);

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const profile = useUserProfile();

  const {
    data: visitPlan,
    isLoading,
  } = useQuery(
    [QK.visitPlan, currentPage, filters],
    () =>
      getVisitPlan(
        currentPage,
        filters.client,
        filters.career,
        filters.location,
        filters.area,
        filters.startDate,
        filters.endDate,
        filters.careerAssigned
      ),
    {
      enabled: !!currentPage || !!filters,
    }
  );

  const totalRecords = visitPlan?.count || 0;
  const totalPages = Math.ceil(totalRecords / 30);

  const handlePageChange = (event, page) => setCurrentPage(page);

  // Modal handlers
  const handleOpenModal = (row) => {
    setVisitId(row.id);
    setShowModal(true);
  };

  const handleUpdateOpenModal = (row) => {
    setVisitId(row.id);
    setShowUpdateModal(true);
    setClientId(row.client);
  };

  const handleScheduleUpdateOpenModal = (row) => {
    setVisitId(row.id);
    setClientId(row.client);
    setArea(row.area_id);
    setShowScheduleUpdateModal(true);
  };

  const handleCloseModal = () => setShowModal(false);
  const handleCloseUpdateModal = () => setShowUpdateModal(false);
  const handleCloseScheduleUpdateModal = () => {
    setShowScheduleUpdateModal(false);
  };

  const handleShowConfirmModal = (row) => {
    setVisitId(row.id);
    setShowConfirmModal(true);
  };

  const handleCloseShowConfirmModal = () => {
    setShowConfirmModal(false);
    setVisitId(null);
  };

  // Delete mutation
  const deleteMutation = useMutation((visitID) => deleteVisitByID(visitID), {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.visitPlan]);
      enqueueSnackbar("Visit deleted successfully", { variant: "success" });
    },
  });

  const handleDeleteVisit = async () => {
    deleteMutation.mutate(visitId);
    handleCloseShowConfirmModal();
  };

  // Helper functions
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-GB");
  };

  const formatTotalMinutes = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours} hour${hours !== 1 ? "s" : ""} ${minutes} minute${minutes !== 1 ? "s" : ""}`;
  };

  // Table configuration
  const columns = [
    {
      field: "visit_name",
      headerName: "Visit Name",
      render: (value, row) => (
        <>
          <Tooltip
            title={row.shift_details?.shift_name || "No Shift Details"}
            arrow
          >
            <Box
              sx={{
                display: "inline-block",
                padding: "5px 10px",
                borderRadius: "8px",
                backgroundColor: row.shift_details?.shift_color || "#ddd",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              {value}
            </Box>
          </Tooltip>
          {row?.is_career_assigned ? (
            <Tooltip title="Career Assigned">
              <IconButton size="small">
                <CheckCircle color="success" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Career Not Assigned">
              <IconButton size="small">
                <PersonAddDisabledRounded color="error" />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
    {
      field: "visit_type_display",
      headerName: "Type",
    },
    {
      field: "client_name",
      headerName: "Service User",
    },
    {
      field: "location",
      headerName: "Location",
    },
    {
      field: "area",
      headerName: "Area",
    },
    {
      field: "start_time",
      headerName: "Start Date",
      render: (value) => formatDate(value),
    },
    {
      field: "end_time",
      headerName: "End Date",
      render: (value) => (value ? formatDate(value) : "-"),
    },
    {
      field: "total_duration",
      headerName: "Total Weekly Hour",
      render: (value) => formatTotalMinutes(value),
    },
  ];

  const actions = [
    {
      icon: <ScheduleIcon />,
      color: "primary",
      tooltip: "Schedule Visit",
      handler: handleOpenModal,
    },
    {
      element: (row) =>
        row.visit_type === 1 ? (
          <Tooltip title="Edit Visit">
            <IconButton
              color="secondary"
              onClick={() => handleUpdateOpenModal(row)}
              sx={{ ml: 1 }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Edit Schedule">
            <IconButton
              color="secondary"
              onClick={() => handleScheduleUpdateOpenModal(row)}
              sx={{ ml: 1 }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        ),
    },
    ...(profile?.user_type === "admin"
      ? [
          {
            icon: <Delete />,
            color: "error",
            tooltip: "Delete Visit",
            handler: handleShowConfirmModal,
          },
        ]
      : []),
  ];

  const getRowSx = (row, index) => ({
    backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#fff",
    textAlign: "center",
  });

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        pb: 4,
        pt: 0,
        px: { xs: 2, sm: 3 },
      }}
    >
      <FilterComponent
        setFilters={setFilters}
        totalRecords={visitPlan?.count || 0}
      />
      <Box>
        <TableComponent
          columns={columns}
          data={visitPlan?.results || []}
          actions={actions}
          isLoading={isLoading}
          pagination={{
            totalPages,
            currentPage,
          }}
          onPageChange={handlePageChange}
          size="small"
          getRowSx={getRowSx}
        />
      </Box>

      {/* Modals */}
      <ScheduleModal
        visitId={visitId}
        show={showModal}
        handleClose={handleCloseModal}
      />

      <UpdateVisitModal
        visitId={visitId}
        show={showUpdateModal}
        clientIdProp={clientId}
        handleClose={handleCloseUpdateModal}
      />

      <EditScheduleModal
        visitId={visitId}
        clientId={clientId}
        show={showScheduleUpdateModal}
        area={area}
        handleClose={handleCloseScheduleUpdateModal}
      />

      <ConfirmModal
        show={showConfirmModal}
        handleClose={handleCloseShowConfirmModal}
        handleConfirm={handleDeleteVisit}
        title="Confirm Delete"
        body="Are you sure you want to delete this visit?"
      />
    </Container>
  );
};

export default VisitPlanScreen;